import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { contactInfo } from "../mock/landingMock";
import { PLATFORM_PAGES, GUIDE_PAGES } from "../data/seoPages";

/**
 * SiteFooter
 *
 * Single site-wide footer used on every page (/, /consulting,
 * /for-quick-commerce, /privacy, /terms, /404). The active audience gets
 * an orange link colour so a returning user always knows where they are.
 *
 * Layout is a five-column grid on desktop and a stacked stack on mobile.
 * Columns: Brand blurb / Product / Resources / Company / Contact.
 */

const FEEDS_URL = "https://feeds.letspickquick.com/";

const linkStyle = {
  color: "#B8B8B8",
  transition: "color 0.2s ease",
  background: "transparent",
  border: "none",
  padding: 0,
  textAlign: "left",
  cursor: "pointer",
  fontSize: "14px",
  display: "block",
  textDecoration: "none",
};

const activeLinkStyle = { ...linkStyle, color: "#F1592B" };

export const SiteFooter = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname || "/";
  const isEndToEnd = pathname === "/";
  const isConsulting = pathname === "/consulting";
  const isPlatforms = pathname === "/for-quick-commerce";

  const goContact = () => {
    if (pathname === "/") {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (pathname === "/consulting") {
      const el = document.getElementById("consulting-cta");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/#contact");
    }
  };

  return (
    <footer
      className="footer"
      data-testid="site-footer"
      style={{ backgroundColor: "#000000" }}
    >
      <style>{`
        .footer a.footer-link:hover, .footer button.footer-link:hover {
          color: #F1592B !important;
        }
        .footer a.footer-accent-link:hover { opacity: 0.85; }
      `}</style>

      <div className="footer-grid">
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => navigate("/")}
            aria-label="Back to PickQuick homepage"
            style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer" }}
          >
            <img
              src="/pickquick-logo.png"
              alt="PickQuick Logo"
              className="h-8 w-auto object-contain"
            />
          </button>
          <p className="max-w-sm" style={{ color: "#B8B8B8", fontSize: "14px", lineHeight: 1.6 }}>
            PickQuick is the internal Quick Commerce division for India&apos;s top FMCG brands.
          </p>
        </div>

        <div className="space-y-3 text-sm">
          <p className="text-xs font-semibold tracking-[0.16em] uppercase" style={{ color: "#8A8A8A" }}>Product</p>
          <div className="space-y-2">
            <a
              href="/"
              className="footer-link"
              data-testid="footer-end-to-end"
              style={isEndToEnd ? activeLinkStyle : linkStyle}
            >
              End-to-End Ops
            </a>
            <a
              href="/consulting"
              className="footer-link"
              data-testid="footer-consulting"
              style={isConsulting ? activeLinkStyle : linkStyle}
            >
              QC Consulting
            </a>
            <a
              href="/for-quick-commerce"
              className="footer-link"
              data-testid="footer-platforms"
              style={isPlatforms ? activeLinkStyle : linkStyle}
            >
              For Platforms
            </a>
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <p className="text-xs font-semibold tracking-[0.16em] uppercase" style={{ color: "#8A8A8A" }}>Resources</p>
          <div className="space-y-2">
            <a
              href={FEEDS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="footer-link"
              data-testid="footer-blog"
              style={linkStyle}
            >
              Blog
            </a>
            <button
              type="button"
              onClick={goContact}
              className="footer-link"
              data-testid="footer-contact"
              style={linkStyle}
            >
              Contact
            </button>
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <p className="text-xs font-semibold tracking-[0.16em] uppercase" style={{ color: "#8A8A8A" }}>Quick Commerce Platforms</p>
          <div className="space-y-2">
            {PLATFORM_PAGES.map((p) => (
              <a
                key={p.slug}
                href={`/${p.slug}`}
                className="footer-link"
                data-testid={`footer-platform-${p.slug}`}
                style={linkStyle}
              >
                {p.label}
              </a>
            ))}
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <p className="text-xs font-semibold tracking-[0.16em] uppercase" style={{ color: "#8A8A8A" }}>Guides</p>
          <div className="space-y-2">
            {GUIDE_PAGES.map((g) => (
              <a
                key={g.slug}
                href={`/${g.slug}`}
                className="footer-link"
                data-testid={`footer-guide-${g.slug.replace(/\//g, "-")}`}
                style={linkStyle}
              >
                {g.label}
              </a>
            ))}
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <p className="text-xs font-semibold tracking-[0.16em] uppercase" style={{ color: "#8A8A8A" }}>Company</p>
          <div className="space-y-2">
            <a href="/privacy" className="footer-link" data-testid="footer-privacy" style={linkStyle}>
              Privacy
            </a>
            <a href="/terms" className="footer-link" data-testid="footer-terms" style={linkStyle}>
              Terms
            </a>
          </div>
        </div>

        <div className="space-y-3 text-sm">
          <p className="text-xs font-semibold tracking-[0.16em] uppercase" style={{ color: "#8A8A8A" }}>Contact</p>
          <div className="space-y-3">
            <div className="flex flex-col gap-1">
              <span style={{ color: "#8A8A8A", fontSize: "13px" }}>Email:</span>
              <a
                href={`mailto:${contactInfo.email}`}
                className="footer-accent-link break-all"
                style={{ color: "#F1592B", fontSize: "14px" }}
                data-testid="footer-email"
              >
                {contactInfo.email}
              </a>
            </div>
            <div className="flex flex-col gap-1">
              <span style={{ color: "#8A8A8A", fontSize: "13px" }}>Phone:</span>
              <a
                href={`tel:${contactInfo.phone.replace(/\s/g, "")}`}
                className="footer-accent-link"
                style={{ color: "#F1592B", fontSize: "14px" }}
                data-testid="footer-phone"
              >
                {contactInfo.phone}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10 max-w-[1200px] mx-auto">
        <div className="space-y-1" style={{ color: "#8A8A8A", fontSize: "11px" }}>
          <p>© {new Date().getFullYear()} PickQuick. All rights reserved.</p>
          <p>Built for fast-moving consumer brands across India.</p>
        </div>
      </div>
    </footer>
  );
};

export default SiteFooter;
